import { StatusBadge } from '@/components/StatusBadge.js';
import { formatCount, formatPercent, truncateMiddle } from '../lib/format.js';
import { seriesColor } from '../lib/series.js';
import { DataRow, DataTable, TableCell, TableHead } from '../ui/DataTable.js';

export type SimulationRow = {
  id: number;
  url: string;
  configuredPct: number;
  effectivePct: number;
  enabled: boolean;
  sampled: number;
};

export type SimulationChartProps = { rows: readonly SimulationRow[]; samples: number };

/**
 * Configured against sampled, per destination.
 *
 * Bars are drawn with a scaled div rather than a chart library: this is one
 * table on one screen, and the comparison is a single proportion. The colour
 * of each bar comes from the shared series scale, so a destination looks the
 * same here as it does in the traffic chart.
 */
export function SimulationChart({ rows, samples }: SimulationChartProps) {
  return (
    <DataTable
      caption={`Configured versus sampled distribution over ${samples} simulated requests`}
      head={
        <>
          <TableHead scope="col">Destination</TableHead>
          <TableHead scope="col">Status</TableHead>
          <TableHead scope="col" className="text-right">
            Configured
          </TableHead>
          <TableHead scope="col" className="text-right">
            Effective
          </TableHead>
          <TableHead scope="col" className="text-right">
            Sampled
          </TableHead>
          <TableHead scope="col" className="w-[30%]">
            <span className="sr-only">Proportion</span>
          </TableHead>
        </>
      }
    >
      {rows.map((row, index) => (
        <DataRow key={row.id}>
          <TableCell className="font-mono text-[length:var(--text-small)]">
            {truncateMiddle(row.url, 56)}
          </TableCell>
          <TableCell>
            <StatusBadge status={row.enabled ? 'active' : 'paused'} />
          </TableCell>
          <TableCell className="text-right tabular-nums">
            {formatPercent(row.configuredPct)}
          </TableCell>
          <TableCell className="text-right tabular-nums text-ink-muted">
            {formatPercent(row.effectivePct)}
          </TableCell>
          <TableCell className="text-right tabular-nums">
            {samples === 0 ? '—' : formatCount(row.sampled)}
          </TableCell>
          <TableCell>
            <div className="h-[8px] w-full rounded-(--radius-sm) bg-surface-sunken">
              <div
                className="h-full rounded-(--radius-sm) transition-transform duration-(--duration-normal) ease-(--ease-out-expo)"
                style={{
                  width: `${Math.max(row.effectivePct, 0)}%`,
                  background: seriesColor(index),
                }}
              />
            </div>
          </TableCell>
        </DataRow>
      ))}
    </DataTable>
  );
}
